import React, { useState, useEffect, useMemo } from 'react';
import { vansAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { GitCompare, Loader2, AlertTriangle, DollarSign, TrendingUp, Receipt, ArrowUpDown, Info, Trophy } from 'lucide-react';
import { toast } from 'sonner';

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
};

// Sorting options for metric cards
const SORT_OPTIONS = [
  { value: 'potential_margin', label: 'Potential Margin' },
  { value: 'inventory_billable', label: 'Billable Value' },
  { value: 'inventory_cost', label: 'Inventory Cost' },
  { value: 'name', label: 'Vehicle Name' },
];

export const Compare = () => {
  const [vans, setVans] = useState([]);
  const [selectedVans, setSelectedVans] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comparing, setComparing] = useState(false);
  
  // Sorting state for metric cards
  const [sortBy, setSortBy] = useState('potential_margin');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    const fetchVans = async () => {
      try {
        const response = await vansAPI.list();
        setVans(response.data.filter(v => v.is_active && !v.archived_at));
      } catch (error) {
        toast.error('Failed to load vehicles');
      } finally {
        setLoading(false);
      }
    };
    fetchVans();
  }, []);

  const toggleVan = (vanId) => {
    setSelectedVans(prev => 
      prev.includes(vanId) 
        ? prev.filter(id => id !== vanId)
        : [...prev, vanId]
    );
    setComparison(null);
  };

  const handleCompare = async () => {
    if (selectedVans.length < 2) {
      toast.error('Select at least 2 vehicles to compare');
      return;
    }
    
    setComparing(true);
    try {
      const response = await vansAPI.compare(selectedVans);
      setComparison(response.data);
    } catch (error) {
      toast.error('Failed to compare vehicles');
    } finally {
      setComparing(false);
    }
  };

  // Calculate highest margin and determine which vans should show badge
  const highestMarginVanIds = useMemo(() => {
    if (!comparison?.vans) return [];
    
    // Get all potential margins, filtering out null/undefined/0
    const vansWithMargin = comparison.vans
      .map(van => ({
        id: van.id,
        margin: van.metrics?.potential_margin || 0
      }))
      .filter(v => v.margin > 0);
    
    if (vansWithMargin.length === 0) return [];
    
    // Find the highest margin
    const maxMargin = Math.max(...vansWithMargin.map(v => v.margin));
    
    // Return all van IDs that have this max margin (handles ties)
    return vansWithMargin
      .filter(v => v.margin === maxMargin)
      .map(v => v.id);
  }, [comparison]);

  // Sort the vans for metric card display
  const sortedVans = useMemo(() => {
    if (!comparison?.vans) return [];
    
    return [...comparison.vans].sort((a, b) => {
      let aVal, bVal;
      
      switch (sortBy) {
        case 'potential_margin':
          aVal = a.metrics?.potential_margin || 0;
          bVal = b.metrics?.potential_margin || 0;
          break;
        case 'inventory_billable':
          aVal = a.metrics?.inventory_billable || 0;
          bVal = b.metrics?.inventory_billable || 0;
          break;
        case 'inventory_cost':
          aVal = a.metrics?.inventory_cost || 0;
          bVal = b.metrics?.inventory_cost || 0;
          break;
        case 'name':
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
          break;
        default:
          return 0;
      }
      
      if (sortBy === 'name') {
        return sortDirection === 'asc' 
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [comparison, sortBy, sortDirection]);

  const toggleSortDirection = () => {
    setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground">Vehicle Comparison</h1>
          <p className="text-muted-foreground mt-1">Compare stock across multiple vehicles</p>
        </div>

        {/* Vehicle Selection */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-bold font-heading">Select Vehicles to Compare</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : vans.length < 2 ? (
              <p className="text-muted-foreground text-center py-8">
                You need at least 2 active vehicles to compare
              </p>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  {vans.map((van) => (
                    <label
                      key={van.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                        selectedVans.includes(van.id)
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <Checkbox
                        checked={selectedVans.includes(van.id)}
                        onCheckedChange={() => toggleVan(van.id)}
                        data-testid={`select-van-${van.id}`}
                      />
                      <span className="font-medium text-sm">{van.name}</span>
                    </label>
                  ))}
                </div>
                <Button
                  onClick={handleCompare}
                  disabled={selectedVans.length < 2 || comparing}
                  className="w-full md:w-auto"
                  data-testid="compare-btn"
                >
                  {comparing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Comparing...
                    </>
                  ) : (
                    <>
                      <GitCompare className="w-4 h-4 mr-2" />
                      Compare ({selectedVans.length} selected)
                    </>
                  )}
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        {/* Comparison Results */}
        {comparison && (
          <>
            {/* Inventory Value Metrics */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-lg font-bold font-heading">Inventory Value by Vehicle</CardTitle>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="w-4 h-4 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent side="right" className="max-w-xs">
                          <p>Snapshot based on current quantities (not actual sales/profit).</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  
                  {/* Sorting Controls */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Sort by:</span>
                    <Select value={sortBy} onValueChange={setSortBy}>
                      <SelectTrigger className="w-40 h-9" data-testid="sort-by-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SORT_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={toggleSortDirection}
                      className="h-9 px-2"
                      data-testid="sort-direction-btn"
                    >
                      <ArrowUpDown className="w-4 h-4 mr-1" />
                      {sortDirection === 'asc' ? 'Asc' : 'Desc'}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-2 md:mt-0">
                  {sortedVans.map((van) => {
                    const hasHighestMargin = highestMarginVanIds.includes(van.id);
                    return (
                      <Card 
                        key={van.id} 
                        className={`border-2 relative ${hasHighestMargin ? 'border-green-500 ring-1 ring-green-500/20' : ''}`}
                        data-testid={`metric-card-${van.id}`}
                      >
                        {/* Highest Margin Badge - in normal flow on mobile, absolute on desktop */}
                        {hasHighestMargin && (
                          <>
                            {/* Mobile: Badge in normal document flow with proper spacing */}
                            <div className="md:hidden flex justify-center pt-3 pb-1">
                              <div 
                                className="px-3 py-1 bg-green-500 text-white text-[10px] font-bold rounded-full flex items-center gap-1 shadow-sm whitespace-nowrap"
                                data-testid={`highest-margin-badge-${van.id}`}
                              >
                                <Trophy className="w-3 h-3 shrink-0" />
                                Highest Potential Margin
                              </div>
                            </div>
                            {/* Desktop: Absolute positioned badge */}
                            <div 
                              className="hidden md:flex absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-green-500 text-white text-xs font-bold rounded-full items-center gap-1 shadow-sm whitespace-nowrap"
                            >
                              <Trophy className="w-3 h-3 shrink-0" />
                              Highest Potential Margin
                            </div>
                          </>
                        )}
                        <CardHeader className={`pb-2 ${hasHighestMargin ? 'pt-1 md:pt-5' : ''}`}>
                          <CardTitle className="text-base font-bold">{van.name}</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <DollarSign className="w-4 h-4" />
                              <span>Inventory Cost</span>
                            </div>
                            <span className="font-mono font-bold text-foreground">
                              {formatCurrency(van.metrics?.inventory_cost)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Receipt className="w-4 h-4" />
                              <span>Inventory Billable Value</span>
                            </div>
                            <span className="font-mono font-bold text-foreground">
                              {formatCurrency(van.metrics?.inventory_billable)}
                            </span>
                          </div>
                          <div className="border-t pt-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <TrendingUp className="w-4 h-4" />
                                <span>Potential Margin (Inventory)</span>
                              </div>
                              <span className={`font-mono font-bold ${
                                (van.metrics?.potential_margin || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                              }`}>
                                {formatCurrency(van.metrics?.potential_margin)}
                              </span>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
                <p className="text-xs text-muted-foreground mt-4 italic">
                  * Values are based on current inventory quantities and do not represent actual sales or realized profit.
                </p>
              </CardContent>
            </Card>

            {/* Item Comparison Table */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-bold font-heading">Item Comparison</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border bg-muted/50">
                        <th className="text-left p-3 font-bold">Item</th>
                        {comparison.vans.map((van) => (
                          <th key={van.id} className="text-center p-3 font-bold min-w-[100px]">
                            {van.name}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {comparison.items.filter(item => 
                        Object.values(item.vans).some(v => v.qty !== 0)
                      ).map((item) => (
                        <tr key={item.item_id} className="border-b border-border hover:bg-muted/30">
                          <td className="p-3">
                            <p className="font-medium">{item.item_name}</p>
                            {item.item_sku && (
                              <p className="text-xs text-muted-foreground font-mono">{item.item_sku}</p>
                            )}
                          </td>
                          {comparison.vans.map((van) => {
                            const data = item.vans[van.id];
                            const qty = data?.qty || 0;
                            const isNegative = qty < 0;
                            return (
                              <td key={van.id} className="p-3 text-center">
                                <span className={`font-mono font-bold ${
                                  isNegative ? 'text-destructive' : qty === 0 ? 'text-muted-foreground' : 'text-foreground'
                                }`}>
                                  {isNegative && <AlertTriangle className="w-3 h-3 inline mr-1" />}
                                  {qty}
                                </span>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                      {comparison.items.filter(item => 
                        Object.values(item.vans).some(v => v.qty !== 0)
                      ).length === 0 && (
                        <tr>
                          <td colSpan={comparison.vans.length + 1} className="p-8 text-center text-muted-foreground">
                            No items with stock in selected vehicles
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
};
